import React, {useEffect, useState} from 'react';
import Typography from "@material-ui/core/Typography";
import {useParams} from "react-router-dom";
import ajax, {getErrorMessage} from "../../ajax/ajax";
import Layout from "../Layout/Layout";
import VideoOrGalleryDetails from "../../components/Upload/VideoOrGalleryDetails";
import {fromRecordedDateString, toRecordedDateString} from "../../util/recordedDate";
import Spinner from "../../components/UI/Spinner/Spinner";
import SortableGrid from "../../components/SortableGrid/SortableGrid";
import Button from "@material-ui/core/Button";
import Message from "../../components/UI/Message/Message";
import {Snackbar} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import useGalleryUploader, {maxVideoDurationSeconds} from "../../hooks/useGalleryUploader";
import GalleryUploadProgress from "../../components/Upload/GalleryUploadProgress";
import Info from "../../components/UI/forms/Guide/Info";

const EditGallery = () => {
  const [form, setForm] = useState(null);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {galleryId} = useParams();

  const {uploads, handleFileEvent, removeUploadingItem, retry, hasPendingMedia} = useGalleryUploader({
    items: images,
    setItems: setImages,
  });

  useEffect(() => {
    if (!galleryId) {
      return;
    }
    const {cancel} = ajax.get(`/api/v1/upload/gallery/${galleryId}`, (data) => {
      setForm({
        name: {error: null, value: data.gallery.title},
        recordedDate: {error: null, value: fromRecordedDateString(data.gallery.recordedDate)},
        year: {error: null, value: data.gallery.year},
        description: {error: null, value: data.gallery.description},
        people: {error: null, value: data.gallery.people},
        places: {error: null, value: data.gallery.places},
        categories: {error: null, value: data.gallery.categories},
        tags: {error: null, value: data.gallery.tags},
      });
      setImages(data.gallery.images.map((image) => {
        return {
          id: image.id,
          databaseId: image.id,
          isPreview: image.isPreview,
          fileName: image.fileName,
          mediaType: image.mediaType,
          status: image.status,
          statusMessage: image.statusMessage,
          duration: image.duration,
          thumbnail: image.small,
          text: image.text,
        };
      }));
    }, (xhrStatusText, xhrStatus, xhr) => {
      setError(getErrorMessage(xhrStatusText, xhrStatus, xhr));
    });

    return () => {
      cancel();
    }
  }, [galleryId]);

  useEffect(() => {
    if (!submitting) {
      return;
    }
    setSuccessMessage(null);
    ajax.post('/api/v1/gallery/'+galleryId, {
      images: images.map((item) => {
        return {
          id: item.databaseId,
          title: '',
          isPreview: item.isPreview,
          text: item.text,
        }
      }),
      name: form.name.value,
      recordedDate: toRecordedDateString(form.recordedDate.value),
      // Still sent, because a gallery that never got a date keeps sorting by its year.
      year: form.year.value,
      description: form.description.value,
      people: form.people.value,
      places: form.places.value,
      categories: form.categories.value,
      tags: form.tags.value,
    }, (data) => {
      setSubmitting(false);
      setError(null);
      setSuccessMessage('Erfolgreich gespeichert.')
    }, (statusText, status, xhr) => {
      setSubmitting(false);
      setError(getErrorMessage(statusText, status, xhr));
    });
  }, [submitting]);

  let content = (<Spinner/>);
  if (null !== form) {
    content = (
      <div className="EditGallery">
        <Typography variant="h1" component="h1" gutterBottom>Galerie bearbeiten</Typography>
        <form>
          <VideoOrGalleryDetails form={form} setForm={setForm} fieldsDisabled={submitting} nameFieldTitle="Name der Galerie" allowYearOnly />
          <input id='fileUpload' type='file' multiple accept="image/*,video/*" style={{display: 'none'}} onChange={handleFileEvent} />
          <label htmlFor='fileUpload'>
            <Button variant="contained" color="secondary" size="medium" component="span" style={{marginBottom: '20px'}}>Weitere Bilder hinzufügen</Button>
          </label>
          <Info>
            Kurze Videos bis {Math.floor(maxVideoDurationSeconds / 60)} Minuten kannst du hier ebenfalls hinzufügen.
          </Info>
          <GalleryUploadProgress uploads={uploads} onRemove={removeUploadingItem} onRetry={retry} />
          <SortableGrid items={images} setItems={setImages} />
          {hasPendingMedia ? (
            <Info>
              Ein Video wird noch verarbeitet. Du kannst schon speichern, das Video erscheint automatisch, sobald es
              fertig ist.
            </Info>
          ) : null}
          {error ? (<Message type="error">{error}</Message>) : null}
          <div>
            <Button variant="contained" color="primary" size="large" onClick={() => {setSubmitting(true);}} style={{marginTop: '15px'}} disabled={submitting}>Speichern</Button>
          </div>
        </form>
        {successMessage ? (<Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => {setSuccessMessage(null);}}>
          <Alert onClose={() => {setSuccessMessage(null);}} severity="success">{successMessage}</Alert>
        </Snackbar>) : null}
      </div>
    );
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
}

export default EditGallery;
